import autosize from 'autosize';
import React, { useContext, useEffect, useRef, useState } from 'react'
import { CompanyContext } from 'contexts/CompanyContext';
import ChatTemplateChatContainer from './components/ChatTemplateChatContainer';
import ChatTemplateChatList from './components/ChatTemplateChatList';
import { api as Api } from 'api/http';
import { AIContext } from 'contexts/AIContext';
import { SessionContext } from 'contexts/SessionContext';
import { replaceAll } from 'utils/replaceAll';
import { useTrendingNews } from 'utils/hooks/useTrendingNews';
import { HiOutlineMenuAlt1 } from 'react-icons/hi';
import { HiMiniPencilSquare } from 'react-icons/hi2';

export default function ChatTemplate() {

  const ref = useRef();
  const { user } = useContext(SessionContext);
  const { company } = useContext(CompanyContext);
  const { getByQueryChat } = useContext(AIContext);
  const [ selectedChatId, setSelectedChatId ] = useState(undefined);
  const [ messages, setMessages ] = useState([]);
  const [ isLoadingChats, setIsLoadingChats ] = useState(false);
  const [ isLoadingMessages, setIsLoadingMessages ] = useState(false);
  const [ isSendingMessage, setIsSendingMessage ] = useState(false); 
  const [ chats, setChats ] = useState([]);
  const { getTrendingNews } = useTrendingNews();
  const [ news, setNews ] = useState();
  const [ chatListOpened, setChatListOpened ] = useState(false);
  const [ dummyRand, setDummyRand ] = useState(Math.random());

  const newSystemMessage = `
    You are the a copywriter expert and marketing expert. You work at ${company?.name}. Your job is to write social media content and help the user with social media and marketing related questions. 
    You must ask the user what do they want to write about.

    Do not make company information up. If you don't know something, you must ask the user to tell you.
    
    Ask relevant questions to the user before writing the requested copy.
    
    You are talking to ${user?.name}, who also works at ${company?.name}.



    ${news?.length ? `These are the latest news related to ${company?.name}'s industry: 
      ${news?.slice(0, Math.min(news?.length, 10))?.map(e => `${e?.title} - source: ${e?.source?.name}. Summary: ${e?.description} \n`)}
    ` : ``}
  `;

  const getChats = async () => {
    setIsLoadingChats(true);
    const _chats = await Api.get(`/aiResultGroups?sort=-id&filter[company_id]=${company?.id}`).then(({ data: chats }) => chats);
    setChats(_chats);
    setIsLoadingChats(false);
  }

  const updateChatTitle = (id, title) => {
    Api.put(`/aiResultGroups/${id}`, {}, { title: replaceAll(title, `"`, ``) }).then(() => {
      getChats();
    });
  }

  const createChat = async (message = "") => { 
    const group = await Api.post(`/aiResultGroups`, {}, { company_id: company?.id }).then(({ data: group }) => group);
    return group;
  }

  const getMessages = async (chatId) => {
    setMessages([]);
    setIsLoadingMessages(true);
    const _messages = await Api.get(`/aiResults?sort=-id&filter[ai_result_group_id]=${chatId}`).then(({ data: _messages }) => _messages);
    setMessages(_messages);
    setIsLoadingMessages(false);
  }

  const sendMessage = async (message) => {
    if (!message?.trim()) return;
    setIsSendingMessage(true);
    try {
      let _messages = [{ query: message, response: undefined }, ...messages];
      setMessages(_messages);
      let groupId = selectedChatId;
      if (selectedChatId === undefined) {
        const group = await createChat(message);
        groupId = group?.id;
        setSelectedChatId(group?.id);
      }
      const group = chats?.find(e => e?.id == groupId);
      const response = await getByQueryChat(message, true, undefined, newSystemMessage, undefined, undefined, groupId);
      _messages[0].response = response;
      setMessages(_messages);
      if (!group?.title) {
        getByQueryChat(`
          Write a conversation title given these first messages of the conversation: User: "${message?.substring(0, Math.min(1000, message?.length))}" - Assistant: "${response?.substring(0, Math.min(1000, response?.length))}". 
          Must be between 3 to 5 words. 
          Conversation title: 
        `).then((title) => {
          updateChatTitle(groupId, title)
        });
      }
    } catch (err) {}
    setIsSendingMessage(false);
  }

  useEffect(() => {
    setDummyRand(Math.random());
    if (selectedChatId === undefined) {
      setMessages([]);
    }
  }, [selectedChatId]);

  useEffect(() => {
    setSelectedChatId(undefined);
    getChats();
  }, [company?.id]);
  
  useEffect(() => {
    if (ref.current) autosize(ref.current);
  }, [ref.current]);

  useEffect(() => {
    if (!localStorage.getItem(`${company?.id}_news_expiry_timestamp`) || localStorage.getItem(`${company?.id}_news_expiry_timestamp`) < new Date().getTime()) {
      getTrendingNews().then(news => {
        setNews(news);
        try {
          localStorage.setItem(`${company?.id}_news_expiry_timestamp`, new Date().getTime() + 1000 * 3600 * 24);
          localStorage.setItem(`${company?.id}_news`, JSON.stringify(news));
        } catch (err) {}
      });
    }
    if (
      localStorage.getItem(`${company?.id}_news_expiry_timestamp`) && 
      localStorage.getItem(`${company?.id}_news_expiry_timestamp`) >= new Date().getTime() && 
      localStorage.getItem(`${company?.id}_news`)
    ) {
      setNews(JSON.parse(localStorage.getItem(`${company?.id}_news`)));
    } 
  }, [company?.id]);

  return (
    <div className='relative -mb-24 lg:mb-0 flex-1 h-[calc(100svh-65px)] lg:h-[calc(100svh-65px)] w-full flex flex-col lg:grid grid-cols-5 overflow-hidden'>
      <div 
        className={`block lg:hidden absolute pointer-events-none opacity-0 w-full h-full top-0 left-0 ${chatListOpened && `opacity-100 pointer-events-auto bg-black/60 z-[1]`}`} 
        onClick={() => setChatListOpened(false)}
      />
      <div 
        className={`transition-all h-full min-h-0 overflow-hidden absolute left-0 top-0 lg:!relative w-[280px] lg:!w-auto lg:max-w-none z-[2] ${!chatListOpened && `!w-0 lg:!w-auto`}`}
        onClick={() => setChatListOpened(false)} 
      >
        <ChatTemplateChatList 
          chats={chats}
          selectedChatId={selectedChatId}
          setSelectedChatId={setSelectedChatId}
          getMessages={getMessages}
          updateChatTitle={updateChatTitle}
        />
      </div>
      <div className='shrink-0 col-span-5 lg:hidden border-b border-tertiary-100 dark:border-gray-600 px-4 gap-8 py-4 bg-secondary-500 dark:bg-gray-800 flex justify-between items-center'>
        <HiOutlineMenuAlt1 
          className='cursor-pointer text-2xl' 
          onClick={() => setChatListOpened(true)} 
        />
        <span className='overflow-hidden text-ellipsis whitespace-nowrap block flex-1 text-center text-tertiary-800 dark:text-white font-semibold'>
          {chats?.find(e => e.id == selectedChatId)?.title || `New Conversation`}
        </span>
        <HiMiniPencilSquare 
          className={`cursor-pointer text-2xl ${!selectedChatId && `invisible`}`} 
          onClick={() => setSelectedChatId(undefined)}
        />
      </div>
      <div className='col-span-5 lg:col-span-4 min-h-0 w-full flex-1'>
        <ChatTemplateChatContainer 
          sendMessage={sendMessage}
          messages={messages}
          isLoadingMessages={isLoadingMessages}
          isSendingMessage={isSendingMessage}
          dummyRand={dummyRand}
        />
      </div>
    </div>
  )
}
